'use client';
import React, { useState, useRef } from 'react';
import type { PublicContactSettings } from '@/lib/contact-settings/schema';
import { ContactInfoBlock } from '@/components/contact/ContactComponents';

const SUBJECTS = [
  { value: 'demo', label: 'Book a product demo' },
  { value: 'pricing', label: 'Pricing question' },
  { value: 'support', label: 'Technical support' },
  { value: 'partnership', label: 'Partnership inquiry' },
  { value: 'billing', label: 'Billing & subscription' },
  { value: 'other', label: 'Other' },
];

interface Props {
  settings: PublicContactSettings;
}

export default function ContactPageClient({ settings }: Props) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    subject: '',
    message: '',
    honeypot: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [consent, setConsent] = useState(false);
  const submitTimeRef = useRef<number>(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.honeypot) return;
    if (Date.now() - submitTimeRef.current < 2000) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim() || !form.subject) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!consent) {
      setError('Please agree to the Privacy Policy so we can process your message.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || null,
          businessName: form.businessName.trim() || null,
          subject: form.subject,
          message: form.message.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send message.');
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2.5 rounded-lg bg-[#0d1120] border border-white/10 text-sm text-white placeholder-[#8b93a3] focus:outline-none focus:border-violet-500/50 transition-colors';

  return (
    <>
      {/* Hero */}
      <section className="pt-28 pb-16 px-4 sm:px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] rounded-full bg-violet-600/6 blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/8 text-violet-300 text-xs font-medium mb-6">
            Get in Touch
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
            We&apos;d Love to Hear From You
          </h1>
          <p className="text-[#9ca3af] text-lg">
            Questions about {settings.companyName}? Our team is based in{' '}
            {[settings.city, settings.country].filter(Boolean).join(', ') || 'Bangkok'} and{' '}
            {settings.responseTimeNote || 'responds within 24 hours'}.
          </p>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-10">
          {/* Left: contact info */}
          <div className="lg:col-span-2">
            <ContactInfoBlock settings={settings} />
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-white/10 bg-[#111827] p-8">
              {success ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
                    <svg
                      className="w-8 h-8 text-emerald-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
                  <p className="text-[#9ca3af] mb-1">
                    {settings.responseTimeNote || "We'll get back to you within 24 hours."}
                  </p>
                  <p className="text-sm text-[#9ca3af]">
                    A confirmation has been sent to your email.{' '}
                    {settings.urgentContactNote && <span>{settings.urgentContactNote}</span>}
                  </p>
                  <button
                    onClick={() => {
                      setSuccess(false);
                      setForm({
                        name: '',
                        email: '',
                        phone: '',
                        businessName: '',
                        subject: '',
                        message: '',
                        honeypot: '',
                      });
                      submitTimeRef.current = Date.now();
                    }}
                    className="mt-6 px-5 py-2.5 rounded-xl border border-white/10 text-sm text-white hover:bg-white/5 transition-colors"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-white mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Honeypot */}
                    <div style={{ display: 'none' }} aria-hidden="true">
                      <input
                        type="text"
                        name="website"
                        value={form.honeypot}
                        onChange={(e) => setForm((f) => ({ ...f, honeypot: e.target.value }))}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="contact-name"
                          className="block text-xs font-medium text-[#9ca3af] mb-1.5"
                        >
                          Full Name *
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          required
                          value={form.name}
                          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                          className={inputClass}
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="contact-email"
                          className="block text-xs font-medium text-[#9ca3af] mb-1.5"
                        >
                          Email *
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                          className={inputClass}
                          placeholder="you@company.com"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="contact-phone"
                          className="block text-xs font-medium text-[#9ca3af] mb-1.5"
                        >
                          Phone
                        </label>
                        <input
                          id="contact-phone"
                          type="text"
                          value={form.phone}
                          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                          className={inputClass}
                          placeholder="+66 81 234 5678"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="contact-business"
                          className="block text-xs font-medium text-[#9ca3af] mb-1.5"
                        >
                          Business Name
                        </label>
                        <input
                          id="contact-business"
                          type="text"
                          value={form.businessName}
                          onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
                          className={inputClass}
                          placeholder="Siam Diamond Co."
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="contact-subject"
                        className="block text-xs font-medium text-[#9ca3af] mb-1.5"
                      >
                        Subject *
                      </label>
                      <select
                        id="contact-subject"
                        required
                        value={form.subject}
                        onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="">Select a topic...</option>
                        {SUBJECTS.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-xs font-medium text-[#9ca3af] mb-1.5"
                      >
                        Message *
                      </label>
                      <textarea
                        id="contact-message"
                        required
                        value={form.message}
                        onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                        rows={5}
                        className={`${inputClass} resize-none`}
                        placeholder="Tell us about your business and what you're looking for..."
                      />
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {error}
                      </div>
                    )}

                    <label
                      htmlFor="contact-consent"
                      className="flex items-start gap-2.5 text-xs text-[#9ca3af] cursor-pointer select-none"
                    >
                      <input
                        id="contact-consent"
                        type="checkbox"
                        required
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 w-4 h-4 flex-shrink-0 accent-violet-500"
                      />
                      <span>
                        I consent to Glanz CRM processing my data in line with the{' '}
                        <a
                          href="/privacy"
                          className="text-violet-400 hover:text-violet-300 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a
                          href="/legal/terms"
                          className="text-violet-400 hover:text-violet-300 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms of Service
                        </a>
                        .
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-60 text-white font-bold text-sm transition-all hover:-translate-y-0.5 disabled:hover:translate-y-0"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        'Send Message'
                      )}
                    </button>

                    {(settings.responseTimeNote || settings.urgentContactNote) && (
                      <p className="text-xs text-center text-[#8b93a3]">
                        {settings.responseTimeNote}
                        {settings.urgentContactNote && <> · {settings.urgentContactNote}</>}
                      </p>
                    )}
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
